/**
 * Recipe API Client
 * Encapsulates all API calls to /recipes endpoints
 */

import { apiRequest } from './queryClient';
import { uploadRecipeImage } from './recipeImageUpload';
import type {
  CreateRecipeRequest,
  UpdateRecipeRequest,
  RecipeSearchParams,
  RecipesResponse,
  ShareRecipeResponse,
  SharedRecipeResponse,
  CopyRecipeResponse,
  ImportUrlRequest,
  ImportImageRequest,
  ImportTextRequest,
  AsyncImportResponse,
  Recipe,
  RecipeImageUploadResult
} from '../types/api-models';

/**
 * Recipe API Client
 * Provides methods for all recipe-related API operations
 */
export class RecipeClient {
  /**
   * Get all recipes with optional search parameters
   */
  static async getRecipes(params?: RecipeSearchParams): Promise<RecipesResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.search?.trim()) {
      searchParams.append("search", params.search.trim());
    }
    
    if (params?.tags?.length) {
      params.tags.forEach(tag => {
        searchParams.append("tags", tag);
      });
    }
    
    if (params?.page !== undefined) {
      searchParams.append("page", params.page.toString());
    }
    
    if (params?.limit !== undefined) {
      searchParams.append("limit", params.limit.toString());
    }

    if (params?.minRating !== undefined) {
      searchParams.append("minRating", params.minRating.toString());
    }

    const url = searchParams.toString() ? `/recipes?${searchParams}` : '/recipes';
    const response = await apiRequest('GET', url);
    return response.json();
  }

  /**
   * Fetch every recipe for the user's space by iterating all pages.
   * Uses the existing paginated endpoint with a large page size to minimise
   * round trips while avoiding one giant request.
   */
  static async getAllRecipes(): Promise<Recipe[]> {
    const PAGE_SIZE = 50;
    const all: Recipe[] = [];
    let page = 1;

    let hasNextPage = true;
    while (hasNextPage) {
      const res = await RecipeClient.getRecipes({ page, limit: PAGE_SIZE });
      all.push(...res.recipes);
      hasNextPage = res.pagination.hasNextPage;
      page++;
    }

    return all;
  }

  /**
   * Fetch only recipes updated since a given ISO timestamp (delta sync).
   * Returns a flat array — not paginated.
   */
  static async getRecipesSince(since: string): Promise<Recipe[]> {
    const response = await apiRequest('GET', `/recipes?updatedSince=${encodeURIComponent(since)}`);
    return response.json();
  }

  /**
   * Get a specific recipe by ID
   */
  static async getRecipe(id: string): Promise<Recipe> {
    const response = await apiRequest('GET', `/recipes/${id}`);
    return response.json();
  }

  /**
   * Create a new recipe
   */
  static async createRecipe(recipe: CreateRecipeRequest): Promise<Recipe> {
    const response = await apiRequest('POST', '/recipes', recipe);
    return response.json();
  }

  /**
   * Update an existing recipe
   */
  static async updateRecipe(id: string, recipe: UpdateRecipeRequest): Promise<Recipe> {
    const response = await apiRequest('PUT', `/recipes/${id}`, recipe);
    return response.json();
  }

  /**
   * Delete a recipe
   */
  static async deleteRecipe(id: string): Promise<void> {
    await apiRequest('DELETE', `/recipes/${id}`);
  }

  /**
   * Update recipe rating (1-5 stars)
   */
  static async updateRecipeRating(
    id: string,
    rating: number
  ): Promise<Recipe> {
    // Validate rating is between 1 and 5
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      throw new Error('Rating must be an integer between 1 and 5');
    }
    const response = await apiRequest('PATCH', `/recipes/${id}/rating`, { rating });
    return response.json();
  }

  /**
   * Share a recipe (generate share link)
   */
  static async shareRecipe(id: string): Promise<ShareRecipeResponse> {
    const response = await apiRequest('POST', `/recipes/${id}/share`);
    return response.json();
  }

  /**
   * Get a shared recipe by token (auth optional)
   */
  static async getSharedRecipe(token: string): Promise<SharedRecipeResponse> {
    const response = await apiRequest('GET', `/recipes/shared/${token}`);
    return response.json();
  }

  /**
   * Copy a shared recipe to user's collection (auth required)
   */
  static async copySharedRecipe(token: string): Promise<CopyRecipeResponse> {
    const response = await apiRequest('POST', `/recipes/shared/${token}/copy`);
    return response.json();
  }

  /**
   * Upload an image for a recipe using the two-step presigned URL flow
   */
  static async uploadRecipeImage(
    recipeId: string, 
    file: File
  ): Promise<RecipeImageUploadResult> {
    return uploadRecipeImage(recipeId, file);
  }

  /**
   * Import recipe from URL (async job)
   */
  static async importFromUrl(request: ImportUrlRequest): Promise<AsyncImportResponse> {
    const response = await apiRequest('POST', '/recipes/import-url', request);
    return response.json();
  }

  /**
   * Import recipe from images (async job)
   */
  static async importFromImages(request: ImportImageRequest): Promise<AsyncImportResponse> {
    const response = await apiRequest('POST', '/recipes/import-image', request);
    return response.json();
  }

  /**
   * Import recipe from text (async job)
   */
  static async importFromText(request: ImportTextRequest): Promise<AsyncImportResponse> {
    const response = await apiRequest('POST', '/recipes/import-text', request);
    return response.json();
  }

  /**
   * Legacy: Import recipe from URL (synchronous - deprecated)
   * @deprecated Use importFromUrl for new implementations
   */
  static async importRecipeFromUrl(url: string): Promise<unknown> {
    const response = await apiRequest('POST', '/recipes/url', { url });
    return response.json();
  }

  /**
   * Get tag groups from the API (for filtering/listing pages)
   */
  static async getTagGroups(): Promise<RecipeTagGroup[]> {
    const response = await apiRequest('GET', '/recipes/tags');
    return response.json();
  }

  /**
   * Get all tag groups from the API (for editing pages)
   */
  static async getAllTagGroups(): Promise<RecipeTagGroup[]> {
    const response = await apiRequest('GET', '/recipes/tags/all');
    return response.json();
  }

}

export interface RecipeTagGroup {
  name: string;
  tags: string[];
}


